/**
 * LocaleServiceLinks.tsx — Wave 38
 *
 * SSR-rendered, same-language internal link block for every non-TH/EN locale
 * landing page. Each locale only links to routes that actually exist for that
 * locale (no 404s, no cross-language dead ends), plus the TH original and the
 * EN mirror so crawlers can walk the whole language graph.
 *
 * Mounted inside the 5 shared multilingual landing components, so all 53
 * locale routes gain contextual outbound links without editing routes.
 */
import { Link, useRouterState } from "@tanstack/react-router";

export type IntlLocale =
  | "zh" | "ja" | "ko" | "ru" | "ar" | "de" | "fr" | "es" | "pt" | "vi" | "id";

/** Slugs that exist as real route files, per locale. Keep in sync with src/routes. */
const ROUTES: Record<IntlLocale, string[]> = {
  ja: ["notary", "notary/faq", "apostille", "translate", "sworn-translator", "mfa-legalisation", "embassy", "marriage", "visa", "services"],
  ko: ["notary", "notary/faq", "apostille", "translate", "sworn-translator", "mfa-legalisation", "embassy", "marriage", "visa", "services"],
  zh: ["notary", "notary/faq", "apostille", "translate", "sworn-translator", "mfa-legalisation", "embassy", "marriage", "visa", "services"],
  de: ["notary", "apostille", "translate", "sworn-translator", "mfa-legalisation", "embassy", "services"],
  fr: ["notary", "sworn-translator", "mfa-legalisation", "embassy"],
  ar: ["notary", "sworn-translator", "mfa-legalisation", "embassy"],
  ru: ["notary", "apostille", "translate", "services"],
  es: ["notary"],
  pt: ["notary"],
  vi: ["notary"],
  id: ["notary"],
};

/** Localized labels per slug. */
const LABELS: Record<IntlLocale, Record<string, string>> = {
  ja: {
    notary: "公証サービス（ノータリー）", "notary/faq": "公証 よくある質問",
    apostille: "アポスティーユ認証", translate: "認証翻訳",
    "sworn-translator": "宣誓翻訳者", "mfa-legalisation": "タイ外務省認証",
    embassy: "大使館領事認証", marriage: "タイでの国際結婚", visa: "タイビザ", services: "サービス一覧",
  },
  ko: {
    notary: "공증 서비스", "notary/faq": "공증 자주 묻는 질문",
    apostille: "아포스티유 인증", translate: "공증 번역",
    "sworn-translator": "선서 번역사", "mfa-legalisation": "태국 외교부 인증",
    embassy: "대사관 영사 확인", marriage: "태국 국제결혼", visa: "태국 비자", services: "전체 서비스",
  },
  zh: {
    notary: "泰国公证服务", "notary/faq": "公证常见问题",
    apostille: "海牙认证（Apostille）", translate: "认证翻译",
    "sworn-translator": "宣誓翻译员", "mfa-legalisation": "泰国外交部认证",
    embassy: "大使馆领事认证", marriage: "在泰国登记结婚", visa: "泰国签证", services: "全部服务",
  },
  de: {
    notary: "Notardienste in Bangkok", apostille: "Apostille & Legalisation",
    translate: "Beglaubigte Übersetzung", "sworn-translator": "Vereidigter Übersetzer",
    "mfa-legalisation": "Legalisation Außenministerium", embassy: "Botschaftslegalisation",
    services: "Alle Leistungen",
  },
  fr: {
    notary: "Services notariaux à Bangkok", "sworn-translator": "Traducteur assermenté",
    "mfa-legalisation": "Légalisation MAE Thaïlande", embassy: "Légalisation consulaire",
  },
  ar: {
    notary: "خدمات التوثيق في بانكوك", "sworn-translator": "مترجم محلّف",
    "mfa-legalisation": "تصديق وزارة الخارجية التايلاندية", embassy: "التصديق القنصلي",
  },
  ru: {
    notary: "Нотариальные услуги", apostille: "Апостиль и легализация",
    translate: "Заверенный перевод", services: "Все услуги",
  },
  es: { notary: "Servicios notariales en Bangkok" },
  pt: { notary: "Serviços notariais em Banguecoque" },
  vi: { notary: "Dịch vụ công chứng tại Bangkok" },
  id: { notary: "Layanan notaris di Bangkok" },
};

const HEADING: Record<IntlLocale, string> = {
  ja: "関連サービス", ko: "관련 서비스", zh: "相关服务",
  de: "Verwandte Leistungen", fr: "Services associés", ar: "خدمات ذات صلة",
  ru: "Смежные услуги", es: "Servicios relacionados", pt: "Serviços relacionados",
  vi: "Dịch vụ liên quan", id: "Layanan terkait",
};

const OTHER: Record<IntlLocale, { th: string; en: string }> = {
  ja: { th: "タイ語版サイト", en: "English site" },
  ko: { th: "태국어 사이트", en: "English site" },
  zh: { th: "泰语版网站", en: "English site" },
  de: { th: "Thai-Version", en: "English site" },
  fr: { th: "Version thaïe", en: "English site" },
  ar: { th: "النسخة التايلاندية", en: "English site" },
  ru: { th: "Тайская версия", en: "English site" },
  es: { th: "Versión en tailandés", en: "English site" },
  pt: { th: "Versão em tailandês", en: "English site" },
  vi: { th: "Bản tiếng Thái", en: "English site" },
  id: { th: "Versi bahasa Thai", en: "English site" },
};

export default function LocaleServiceLinks() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const parts = pathname.replace(/^\/+|\/+$/g, "").split("/");
  const locale = parts[0] as IntlLocale;
  if (!ROUTES[locale]) return null;

  const current = parts.slice(1).join("/") || "notary";
  const slugs = (ROUTES[locale] ?? []).filter((s) => s !== current);
  const labels = LABELS[locale] ?? {};
  const other = OTHER[locale];
  const rtl = locale === "ar";


  return (
    <nav
      aria-label={HEADING[locale]}
      dir={rtl ? "rtl" : undefined}
      className="mx-auto max-w-5xl px-4 pb-14 sm:px-6"
    >
      <h2 className="text-lg font-bold text-navy-deep">{HEADING[locale]}</h2>
      <ul className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {slugs.map((s) => (
          <li key={s}>
            <Link
              to={`/${locale}/${s}` as never}
              className="block rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 transition hover:border-gold hover:text-navy-deep"
            >
              {labels[s] ?? s}
            </Link>
          </li>
        ))}
        <li>
          <Link
            to={`/${current === "notary/faq" ? "notary" : current}` as never}
            className="block rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-700 transition hover:border-gold hover:text-navy-deep"
          >
            {other.th}
          </Link>
        </li>
        <li>
          <Link
            to={`/en/${current === "notary/faq" ? "notary" : current}` as never}
            className="block rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-700 transition hover:border-gold hover:text-navy-deep"
          >
            {other.en}
          </Link>
        </li>
      </ul>
    </nav>
  );
}
